import { parseToolCall } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/parsers";
import {
  cancelToolCall,
  runToolCall,
} from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/runners";
import {
  type ParsedToolCall,
  type ToolCallProgressStatus,
} from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/types";
import type {
  AgentId,
  ConversationMessage,
  ConversationToolMessage,
  ToolCall,
} from "@mirage/service-conversation/types";
import { tagged } from "@mirage/service-logging";
import { useCallback, useEffect, useMemo, useState } from "react";

const logger = tagged("ToolCallsRunner");

interface ToolCallWithStatus {
  toolCall: ParsedToolCall;
  /** progress status for the tool call, undefined if not started */
  status: ToolCallProgressStatus | undefined;
}

// map of tool call id to tool call with status
type ToolCallsWithStatus = {
  [toolCallId: string]: ToolCallWithStatus;
};

export interface ToolCallsRunner {
  /**
   * The tool calls with their current progress statuses.
   */
  toolCalls: ToolCallsWithStatus;

  /**
   * Run the tool calls. This will automatically invoke onToolCallsCompletion when all tool calls
   * have been run (successfully or not).
   */
  runToolCalls: () => Promise<void>;

  /**
   * Get the messages we should send back to the server for cancelling tool calls.
   * This may be invoked when the user sends a chat message rather than accepting the tool calls.
   */
  cancelToolCalls: () => ConversationToolMessage[];
}

/**
 * A hook to help run tool calls + manage progress statuses.
 *
 * @param toolCalls - The tool calls to run.
 * @param onToolCallsCompletion - A callback to invoke after the tool calls have been run.
 */
export function useToolCallsRunner(
  agentId: AgentId | undefined,
  toolCalls: ToolCall[],
  onToolCallsCompletion: (
    toolMessages: ConversationToolMessage[],
    agentId: AgentId | undefined,
  ) => void,
): ToolCallsRunner {
  const [toolCallsWithStatus, setToolCallsWithStatus] = useState<{
    [toolCallId: string]: ToolCallWithStatus;
  }>({});

  // reset tool calls when the tool calls change
  useEffect(() => {
    const parsedToolCalls = parseToolCalls(toolCalls, agentId);
    const newToolCallsWithStatus: ToolCallsWithStatus = {};
    for (const toolCall of parsedToolCalls) {
      newToolCallsWithStatus[toolCall.id] = { toolCall, status: undefined };
    }
    setToolCallsWithStatus(newToolCallsWithStatus);
  }, [toolCalls, agentId]);

  const handleRunToolBlocks = useCallback(async () => {
    const toolMessages: ConversationToolMessage[] = [];
    for (const toolCallWithStatus of Object.values(toolCallsWithStatus)) {
      setToolCallsWithStatus((prev) =>
        updateToolCallsWithStatus(prev, toolCallWithStatus.toolCall, "running"),
      );
      try {
        const responseText = await runToolCall(toolCallWithStatus.toolCall);
        toolMessages.push({
          case: "toolMessage",
          value: {
            toolCallId: toolCallWithStatus.toolCall.id,
            name: toolCallWithStatus.toolCall.name,
            content: responseText,
            text: responseText,
          },
        });
        setToolCallsWithStatus((prev) =>
          updateToolCallsWithStatus(
            prev,
            toolCallWithStatus.toolCall,
            "success",
          ),
        );
      } catch (error) {
        setToolCallsWithStatus((prev) =>
          updateToolCallsWithStatus(prev, toolCallWithStatus.toolCall, "error"),
        );
        logger.error("runToolCall failed", error, toolCallWithStatus.toolCall);
        const errorText = `Error running tool call: ${toolCallWithStatus.toolCall.name}`;
        toolMessages.push({
          case: "toolMessage",
          value: {
            toolCallId: toolCallWithStatus.toolCall.id,
            name: toolCallWithStatus.toolCall.name,
            content: errorText,
            text: errorText,
          },
        });
        break; // stop running other tool calls if one fails
      }
    }

    // all done, invoke the callback with the tool results
    onToolCallsCompletion(toolMessages, agentId);
  }, [agentId, onToolCallsCompletion, toolCallsWithStatus]);

  const handleCancelToolCalls = useCallback(() => {
    // mark all tool calls as cancelled
    const newToolCallsWithStatus: ToolCallsWithStatus = {};
    for (const toolCallWithStatus of Object.values(toolCallsWithStatus)) {
      newToolCallsWithStatus[toolCallWithStatus.toolCall.id] = {
        toolCall: toolCallWithStatus.toolCall,
        status: "cancelled",
      };
    }

    // get the cancel messages for each tool call
    const cancelMessages: ConversationToolMessage[] = [];
    for (const toolCallWithStatus of Object.values(toolCallsWithStatus)) {
      const responseText = cancelToolCall(toolCallWithStatus.toolCall);
      cancelMessages.push({
        case: "toolMessage",
        value: {
          toolCallId: toolCallWithStatus.toolCall.id,
          name: toolCallWithStatus.toolCall.name,
          content: responseText,
          text: responseText,
        },
      });
    }
    return cancelMessages;
  }, [toolCallsWithStatus]);

  return useMemo(
    () => ({
      toolCalls: toolCallsWithStatus,
      runToolCalls: handleRunToolBlocks,
      cancelToolCalls: handleCancelToolCalls,
    }),
    [toolCallsWithStatus, handleRunToolBlocks, handleCancelToolCalls],
  );
}

function parseToolCalls(
  toolCalls: ToolCall[],
  agentId: AgentId | undefined,
): ParsedToolCall[] {
  const parsedToolCalls: ParsedToolCall[] = [];
  for (const toolCall of toolCalls) {
    const parsedToolCall = parseToolCall(toolCall, agentId);
    if (parsedToolCall) {
      parsedToolCalls.push(parsedToolCall);
    }
  }
  return parsedToolCalls;
}

function updateToolCallsWithStatus(
  prev: ToolCallsWithStatus,
  toolCall: ParsedToolCall,
  status: ToolCallProgressStatus,
): ToolCallsWithStatus {
  if (toolCall.id in prev) {
    return {
      ...prev,
      [toolCall.id]: { toolCall, status },
    };
  } else {
    // if tool call completed after tool calls were already reset, ignore it
    return prev;
  }
}

const EMPTY_TOOL_CALLS: ToolCall[] = [];

export function useConversationToolCallsRunner(
  messages: ConversationMessage[],
  onToolCallsCompletion: (
    toolMessages: ConversationToolMessage[],
    agentId: AgentId | undefined,
  ) => void,
): ToolCallsRunner {
  const { toolCalls, agentId } = useMemo(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.messageType.case === "assistantMessage") {
      const assistantMessage = lastMessage.messageType.value;
      return {
        toolCalls: assistantMessage.toolCalls || EMPTY_TOOL_CALLS,
        agentId: assistantMessage.agentId as AgentId | undefined,
      };
    }
    return {
      toolCalls: EMPTY_TOOL_CALLS,
      agentId: undefined,
    };
  }, [messages]);
  return useToolCallsRunner(agentId, toolCalls, onToolCallsCompletion);
}
